import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../config";

const ConsentForm = () => {
  const navigate = useNavigate();
  const { consentToken } = useParams();
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [vaccinationAnswers, setVaccinationAnswers] = useState({});

  // Create axios instance with base URL
  const axiosInstance = axios.create({
    baseURL: config.API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Fetch form questions based on token
  useEffect(() => {
    const fetchFormQuestions = async () => {
      try {
        if (!consentToken) {
          toast.error("Invalid consent token", { position: "top-center" });
          setLoading(false);
          return;
        }

        const encodedToken = encodeURIComponent(consentToken);
        const response = await axiosInstance.get(
          `consentForm/getConsentFormData/${encodedToken}`
        );

        if (response.data && response.data.status) {
          const fetchedQuestions = response.data.data || [];
          setQuestions(fetchedQuestions);
          
          // Initialize vaccination answers
          const initialVaccinationAnswers = {};
          fetchedQuestions.forEach(question => {
            if (question.answerType === "vaccination_details") {
              const initialChecked = {};
              question.vaccinationList.forEach(vaccine => {
                initialChecked[vaccine.id] = vaccine.checked || false;
              });
              initialVaccinationAnswers[question._id] = initialChecked;
            }
          });
          setVaccinationAnswers(initialVaccinationAnswers);
        } else {
          toast.error("Failed to load consent form", { position: "top-center" });
        }
      } catch (error) {
        console.error("Error fetching form questions:", error);
        toast.error(error.response?.data?.message || error.message || "Failed to load consent form", { position: "top-center" });
      } finally {
        setLoading(false);
      }
    };

    fetchFormQuestions();
  }, [consentToken]);

  // Handle input changes for text/textarea questions
  const handleInputChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Handle checkbox changes for vaccination list
  const handleVaccinationChange = (questionId, vaccineId, checked) => {
    setVaccinationAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [vaccineId]: checked
      }
    }));
  };

  // Handle select all/none for vaccination list
  const handleSelectAllVaccinations = (questionId, question, selectAll) => {
    const updatedVaccinations = {};
    question.vaccinationList.forEach(vaccine => {
      updatedVaccinations[vaccine.id] = selectAll;
    });
    
    setVaccinationAnswers(prev => ({
      ...prev,
      [questionId]: updatedVaccinations
    }));
  };

  // Prepare answer data for submission
  const prepareQuestionAnswers = () => {
    const questionAnswers = [];

    questions.forEach(question => {
      if (question.answerType === "vaccination_details") {
        const selectedVaccinations = [];
        const vaccineStatus = vaccinationAnswers[question._id] || {};
        
        question.vaccinationList.forEach(vaccine => {
          if (vaccineStatus[vaccine.id]) {
            selectedVaccinations.push(vaccine.name);
          }
        });

        questionAnswers.push({
          question_id: question._id,
          question: question.question,
          answer: selectedVaccinations
        });
      } else {
        // For other question types (text, etc.)
        const answer = formData[question._id] || "";
        if (answer || question.isRequired) {
          questionAnswers.push({
            question_id: question._id,
            question: question.question,
            answer: answer
          });
        }
      }
    });

    return questionAnswers;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required questions
    const unansweredRequiredQuestions = questions.filter(question => {
      if (!question.isRequired) return false;
      
      if (question.answerType === "vaccination_details") {
        const vaccineStatus = vaccinationAnswers[question._id] || {};
        const hasSelection = Object.values(vaccineStatus).some(status => status === true);
        return !hasSelection;
      } else {
        return !formData[question._id];
      }
    });

    if (unansweredRequiredQuestions.length > 0) {
      toast.error("Please answer all required questions", { position: "top-center" });
      return;
    }

    try {
      const questionAnswers = prepareQuestionAnswers();
      
      const payload = {
        key: consentToken, // The token from URL
        questionAnswers: questionAnswers
      };

      const response = await axiosInstance.post(
        "consentForm/addConsentFormData",
        payload
      );

      if (response.data && response.data.status) {
        toast.success("Consent form submitted successfully!", { 
          position: "top-center",
          autoClose: 2000,
          onClose: () => {
            // Redirect to the specified URL after successful submission
            window.location.href = "https://findlocalpetservices.com/";
          }
        });
        
      } else {
        toast.error(response.data?.message || "Failed to submit form", { 
          position: "top-center" 
        });
      }
    } catch (error) {
      console.error("Error submitting consent form:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to submit consent form", { 
        position: "top-center" 
      });
    }
  };

  // Render different question types
  const renderQuestion = (question) => {
    const { _id, question: questionText, answerType, isRequired, vaccinationList = [] } = question;

    if (answerType === "vaccination_details") {
      const vaccineStatus = vaccinationAnswers[_id] || {};
      const allSelected = vaccinationList.length > 0 && 
                         vaccinationList.every(vaccine => vaccineStatus[vaccine.id] === true);

      return (
        <div key={_id} className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <label className="block text-lg font-semibold text-gray-800">
              {questionText} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <button
              type="button"
              onClick={() => handleSelectAllVaccinations(_id, question, !allSelected)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          
          <div className="space-y-3">
            {vaccinationList.map(vaccine => (
              <div key={vaccine.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`${_id}-${vaccine.id}`}
                  checked={vaccineStatus[vaccine.id] || false}
                  onChange={(e) => handleVaccinationChange(_id, vaccine.id, e.target.checked)}
                  className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <label 
                  htmlFor={`${_id}-${vaccine.id}`}
                  className="ml-3 text-gray-700 cursor-pointer"
                >
                  {vaccine.name}
                </label>
              </div>
            ))}
          </div>
          
          {isRequired && !Object.values(vaccineStatus).some(status => status === true) && (
            <p className="mt-2 text-sm text-red-500">Please select at least one vaccination</p>
          )}
        </div>
      );
    }

    // Default text input for other question types
    return (
      <div key={_id} className="mb-6">
        <label htmlFor={_id} className="block text-sm font-medium text-gray-700 mb-2">
          {questionText} {isRequired && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          id={_id}
          value={formData[_id] || ''}
          onChange={(e) => handleInputChange(_id, e.target.value)}
          required={isRequired}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          placeholder={`Enter ${questionText.toLowerCase()}`}
        />
      </div>
    );
  };

  // Loading state HTML (replacing LoaderSpiner component)
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-800 mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Loading Consent Form</h2>
          <p className="text-gray-500">Please wait while we load the form...</p>
        </div>
      </div>
    );
  }

  if (!questions.length && !loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Form Not Found</h1>
          <p className="text-gray-600 mb-6">The consent form you're looking for is invalid or has expired.</p>
          <button
            onClick={() => window.location.href = "https://findlocalpetservices.com/"}
            className="px-6 py-3 bg-blue-800 text-white font-medium rounded-lg hover:bg-blue-900 transition w-full"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Consent Form
          </h1>
          <p className="text-gray-600 text-lg">
            Please provide the required information below
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Render questions */}
            {questions.map(question => renderQuestion(question))}

            {/* Submit Section */}
            <div className="pt-8 border-t border-gray-200 mt-8">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <p className="text-sm text-gray-500">
                  {questions.filter(q => q.isRequired).length} required field(s)
                </p>
                
                <div className="flex gap-4">
                  <button 
                    type="button" 
                    onClick={() => navigate(-1)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition duration-200"
                  >
                    Back
                  </button>
                  <button 
                    type="submit"
                    className="px-6 py-3 bg-blue-800 text-white font-medium rounded-lg hover:bg-blue-900 transition duration-200"
                  >
                    Submit Form
                  </button>
                </div>
              </div>
              
              <p className="mt-4 text-sm text-gray-500 text-center">
                By submitting this form, you agree to the terms and conditions of our service.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConsentForm;